'use client';

import { forwardRef, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { tv } from 'tailwind-variants';

import { faCloudArrowUp, faXmark } from '@icons/classic/regular';
import { Body } from '../typography/Body';

import type { ChangeEvent, DragEvent } from 'react';

const dropzone = tv({
  base: `
    relative
    flex
    flex-col
    items-center
    justify-center
    w-full
    min-h-32
    rounded-lg
    border-2
    border-dashed
    border-border-primary
    bg-background-secondary
    cursor-pointer
    transition-all
    duration-200
    focus-visible:outline-none
    focus-visible:ring-2
    focus-visible:ring-primary
    focus-visible:ring-offset-2
  `,
  variants: {
    isDragOver: {
      true: 'border-accent-primary bg-background-accent-secondary',
    },
    hasFile: {
      true: 'border-border-positive bg-background-positive-secondary',
    },
    isInvalid: {
      true: 'border-border-negative bg-background-negative-secondary',
    },
    isDisabled: {
      true: 'cursor-not-allowed opacity-50',
    },
  },
});

const EXISTING_FILE_LABEL = 'File already uploaded';

const iconContainer = tv({
  base: 'mb-2 text-content-tertiary',
  variants: {
    isDragOver: {
      true: 'text-content-accent',
    },
    hasFile: {
      true: 'text-content-positive',
    },
  },
});

interface DropzoneInputProps {
  /** Accepted file types (e.g., 'image/*', '.pdf,.doc') */
  accept?: string;
  /** Custom label for the dropzone */
  label?: string;
  /** Helper text shown below the label */
  helperText?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /**
   * Current file value. A `File` reflects a fresh selection made this session.
   * A `string` reflects a file uploaded in a prior session (its filename, or an
   * opaque reference we can't render a name for) — still renders the "has a file"
   * state without requiring the user to re-select anything.
   */
  value?: File | string | null;
  /** Callback when file changes */
  onChange?: (file: File | null) => void;
  /** Callback fired with a file the user picked/dropped that doesn't match `accept` */
  onReject?: (file: File) => void;
  /** Callback when input loses focus */
  onBlur?: () => void;
  /** Error state */
  'aria-invalid'?: boolean;
  /** ID for the input */
  id?: string;
  /** Aria label for accessibility */
  'aria-label'?: string;
  /** Aria describedby for accessibility */
  'aria-describedby'?: string;
  /** Additional class name */
  className?: string;
}

const DropzoneInput = forwardRef<HTMLInputElement, DropzoneInputProps>(function DropzoneInput(
  {
    accept,
    label = 'Click to upload or drag and drop',
    helperText,
    disabled = false,
    value,
    onChange,
    onReject,
    onBlur,
    'aria-invalid': isInvalid,
    id,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    className,
  },
  ref
) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Merge refs
  const setRefs = (element: HTMLInputElement | null) => {
    inputRef.current = element;
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (isFileTypeAccepted(file, accept)) {
        onChange?.(file);
      } else {
        onReject?.(file);
      }
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (isFileTypeAccepted(file, accept)) {
        onChange?.(file);
      } else {
        onReject?.(file);
      }
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const hasFile = Boolean(value);
  // A plain filename string (no path separators) is safe to show as-is; anything
  // else stored as a string (e.g. a base64 data URL or opaque reference) falls
  // back to a generic label so we don't render binary data as a "filename".
  const displayName =
    value instanceof File ? value.name : typeof value === 'string' && /^[^/\\:]+$/.test(value) ? value : EXISTING_FILE_LABEL;

  return (
    <div
      className={dropzone({
        isDragOver,
        hasFile,
        isInvalid: Boolean(isInvalid),
        isDisabled: disabled,
        className,
      })}
      aria-describedby={ariaDescribedBy}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      onClick={handleClick}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
    >
      <input
        className="sr-only"
        accept={accept}
        aria-hidden="true"
        disabled={disabled}
        id={id}
        onBlur={onBlur}
        onChange={handleFileChange}
        ref={setRefs}
        tabIndex={-1}
        type="file"
      />

      <div className={iconContainer({ isDragOver, hasFile })}>
        <FontAwesomeIcon className="h-8 w-8" icon={faCloudArrowUp} />
      </div>

      {hasFile ? (
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2">
            <Body className="text-content-primary" size="md" weight="medium">
              {displayName}
            </Body>
            {value instanceof File && (
              <button
                className="rounded p-1 text-content-tertiary transition-colors hover:bg-background-tertiary hover:text-content-primary"
                aria-label="Remove file"
                onClick={handleClear}
                type="button"
              >
                <FontAwesomeIcon className="h-4 w-4" icon={faXmark} />
              </button>
            )}
          </div>
          <Body className="mt-1 text-content-secondary" size="xs">
            Click to replace or drag and drop
          </Body>
        </div>
      ) : (
        <>
          <Body className="text-content-secondary" size="md">
            {isDragOver ? 'Drop file here' : label}
          </Body>
          {helperText && (
            <Body className="mt-1 text-content-tertiary" size="sm">
              {helperText}
            </Body>
          )}
        </>
      )}
    </div>
  );
});

/**
 * Check if a file type matches the accept string
 */
function isFileTypeAccepted(file: File, accept?: string): boolean {
  if (!accept) return true;

  const acceptTypes = accept.split(',').map((type) => type.trim().toLowerCase());
  const fileType = file.type.toLowerCase();
  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

  return acceptTypes.some((acceptType) => {
    // Handle wildcards like 'image/*'
    if (acceptType.endsWith('/*')) {
      const category = acceptType.replace('/*', '');
      return fileType.startsWith(category);
    }
    // Handle MIME types like 'image/png'
    if (acceptType.includes('/')) {
      return fileType === acceptType;
    }
    // Handle extensions like '.pdf'
    return fileExtension === acceptType;
  });
}

export { DropzoneInput, dropzone };
export type { DropzoneInputProps };
